import React, { useState, useEffect } from 'react'
import { Box } from '@mui/material'
import { usePaymentContext } from '../contexts/PaymentContext'
import PageBanner from '../components/PageBanner'
import SMBSelector from '../components/SMBSelector'
import { BillingSkeleton } from '../components/skeletons'
import {
  PlanSelectionGrid,
  ActiveSubscriptionCard,
  AICreditBalanceCard,
  BillingHistoryTable,
  TopUpModal,
  CancelSubscriptionModal,
  PaymentProcessingModal,
} from '../components/payment'

export const Billing: React.FC = () => {
  const {
    smbs,
    selectedSmbId,
    setSmbId,
    summary,
    plans,
    loading,
    isRefreshing,
    checkoutLoading,
    pollingStatus,
    activeSubscription,
    hasGrowthSubscription,
    isEssentials,
    hasAnySubscription,
    quotaBuckets,
    handleSelectPlan,
    handlePurchaseTopUp,
    handleConfirmCancel,
    refreshBilling,
  } = usePaymentContext()

  const [cancelModalOpen, setCancelModalOpen] = useState(false)
  const [cancelTargetId, setCancelTargetId] = useState<string | undefined>(undefined)
  const [topUpModalOpen, setTopUpModalOpen] = useState(false)

  // Always refresh billing data when the page is visited
  useEffect(() => {
    refreshBilling()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <BillingSkeleton />
  }

  return (
    <Box>
      <PageBanner
        title="Billing & Plans"
        subtitle="Manage your plan, check AI credits balance, and view payment history."
        onRefresh={refreshBilling}
        refreshing={isRefreshing}
      />

      <SMBSelector
        smbs={smbs}
        selectedSMBId={selectedSmbId}
        onChange={setSmbId}
      />

      {hasAnySubscription && (
        <ActiveSubscriptionCard
          activeSubscription={activeSubscription}
          isEssentials={isEssentials}
          hasGrowthSubscription={hasGrowthSubscription}
          plans={plans}
          onUpgradeClick={handleSelectPlan}
          loading={checkoutLoading}
          onCancelClick={(subId) => {
            setCancelTargetId(subId)
            setCancelModalOpen(true)
          }}
        />
      )}

      {!hasAnySubscription && (
        <Box id="billing-plans" sx={{ mb: 4, position: 'relative', scrollMarginTop: '80px' }}>
          <PaymentProcessingModal
            open={checkoutLoading || Boolean(pollingStatus)}
            status={pollingStatus}
            mode={pollingStatus ? 'processing' : 'redirecting'}
          />
          <PlanSelectionGrid
            plans={plans}
            onSelectPlan={handleSelectPlan}
            loading={checkoutLoading}
          />
        </Box>
      )}

      <AICreditBalanceCard
        hasGrowthSubscription={hasGrowthSubscription}
        quotaBuckets={quotaBuckets}
        onBuyTopUpClick={() => setTopUpModalOpen(true)}
      />

      <Box id="billing-history" sx={{ scrollMarginTop: '80px' }}>
        <BillingHistoryTable summary={summary} plans={plans} />
      </Box>

      <TopUpModal
        open={topUpModalOpen}
        onClose={() => {
          if (!checkoutLoading) setTopUpModalOpen(false)
        }}
        onPurchase={async (amount, units) => {
          try {
            await handlePurchaseTopUp(amount, units)
          } finally {
            setTopUpModalOpen(false)
          }
        }}
        loading={checkoutLoading}
      />

      <CancelSubscriptionModal
        open={cancelModalOpen}
        onClose={() => {
          if (!checkoutLoading) {
            setCancelModalOpen(false)
            setCancelTargetId(undefined)
          }
        }}
        onConfirm={async () => {
          try {
            await handleConfirmCancel(cancelTargetId)
          } finally {
            setCancelModalOpen(false)
            setCancelTargetId(undefined)
          }
        }}
        loading={checkoutLoading}
      />

      <PaymentProcessingModal open={Boolean(pollingStatus)} status={pollingStatus} />
    </Box>
  )
}

export default Billing
